import { useEffect, useRef, useState, type KeyboardEvent } from "react";

export type ChatLang = "hr" | "en";

export type ChatPurposeKind = "general" | "decision" | "department";

export interface ChatPurpose {
  kind: ChatPurposeKind;
  label?: string;
}

export type ChatStatusKind = "ready" | "working" | "off" | "notready" | "uncertain";

export interface ChatStatus {
  kind: ChatStatusKind;
  text: string;
}

export interface ChatProject {
  id: string;
  name: string;
  agent: boolean;
}

export interface ChatHeaderProps {
  projectName: string;
  conversationTitle: string;
  purpose?: ChatPurpose;
  status: ChatStatus;
  lang: ChatLang;
  onToggleSearch: () => void;
  onToggleInfo: () => void;
  projects: ChatProject[];
  currentProjectId: string;
  onSelectProject: (id: string) => void;
  searchOpen: boolean;
  infoOpen: boolean;
}

const statusDotClass: Record<ChatStatusKind, string> = {
  ready: "bg-green-500",
  working: "bg-amber-500",
  uncertain: "bg-amber-500",
  off: "bg-muted-foreground",
  notready: "bg-red-500",
};

const iconButtonClass =
  "inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

function MagnifyIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4 shrink-0"
    >
      <circle cx="9" cy="9" r="5.5" />
      <line x1="13.2" y1="13.2" x2="17" y2="17" />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4 shrink-0"
    >
      <circle cx="10" cy="10" r="7" />
      <line x1="10" y1="9" x2="10" y2="14" />
      <circle cx="10" cy="6.25" r="0.9" fill="currentColor" stroke="none" />
    </svg>
  );
}

export function ChatHeader({
  projectName,
  conversationTitle,
  purpose,
  status,
  lang,
  onToggleSearch,
  onToggleInfo,
  projects,
  currentProjectId,
  onSelectProject,
  searchOpen,
  infoOpen,
}: ChatHeaderProps) {
  const switchRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuOpen, setMenuOpen] = useUncontrolledToggle();
  const closeMenu = () => setMenuOpen(false);

  useEffect(() => {
    if (!menuOpen) return;
    const onPointer = (event: Event) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (menuRef.current?.contains(target)) return;
      if (switchRef.current?.contains(target)) return;
      setMenuOpen(false);
    };
    document.addEventListener("pointerdown", onPointer, true);
    document.addEventListener("mousedown", onPointer, true);
    return () => {
      document.removeEventListener("pointerdown", onPointer, true);
      document.removeEventListener("mousedown", onPointer, true);
    };
  }, [menuOpen]);

  const effectivePurpose: ChatPurpose = purpose ?? { kind: "general" };

  const t = {
    search: lang === "en" ? "Search the conversation" : "Traži u razgovoru",
    info: lang === "en" ? "Conversation information" : "Informacije o razgovoru",
    projectsDialog: lang === "en" ? "Projects and conversations" : "Projekti i razgovori",
    mainConversation: lang === "en" ? "Main conversation" : "Glavni razgovor",
    agentUnavailable: lang === "en" ? "Agent not available" : "Agent nije dostupan",
    newConversation: lang === "en" ? "New conversation · coming soon" : "Novi razgovor · uskoro",
  };

  const purposeLabel = purposeChipText(effectivePurpose, lang);

  function handleMenuKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === "Escape") {
      event.preventDefault();
      setMenuOpen(false);
      switchRef.current?.focus();
    }
  }

  function handleSelect(id: string) {
    if (id !== currentProjectId) {
      onSelectProject(id);
    }
    setMenuOpen(false);
  }

  return (
    <header
      data-testid="chat-header"
      className="relative shrink-0 border-b border-border bg-background px-3 py-2"
    >
      <div className="flex min-w-0 items-center gap-1">
        <button
          ref={switchRef}
          type="button"
          data-testid="chat-switch"
          aria-expanded={menuOpen}
          aria-haspopup="dialog"
          onClick={() => setMenuOpen((open) => !open)}
          className="flex min-h-11 min-w-0 flex-1 items-center gap-1 text-left"
        >
          <span data-testid="chat-project" className="truncate font-semibold">
            {projectName}
          </span>
          <span aria-hidden="true" className="shrink-0 text-muted-foreground">
            ›
          </span>
          <span data-testid="chat-title" className="truncate text-muted-foreground">
            {conversationTitle}
          </span>
        </button>
        <button
          type="button"
          data-testid="chat-search-toggle"
          aria-label={t.search}
          title={t.search}
          aria-pressed={searchOpen}
          onClick={() => {
            closeMenu();
            onToggleSearch();
          }}
          className={`${iconButtonClass} ${searchOpen ? "bg-muted text-foreground" : ""}`}
        >
          <MagnifyIcon />
        </button>
        <button
          type="button"
          data-testid="chat-info-toggle"
          aria-label={t.info}
          title={t.info}
          aria-pressed={infoOpen}
          onClick={() => {
            closeMenu();
            onToggleInfo();
          }}
          className={`${iconButtonClass} ${infoOpen ? "bg-muted text-foreground" : ""}`}
        >
          <InfoIcon />
        </button>
      </div>

      <div className="flex min-w-0 items-center gap-2 overflow-hidden pt-1 text-xs text-muted-foreground">
        <span
          data-testid="chat-purpose"
          className="min-w-0 max-w-full truncate rounded-full border border-border px-2 py-0.5"
        >
          {purposeLabel}
        </span>
        <span
          data-testid="chat-status"
          data-kind={status.kind}
          role="status"
          className="flex min-w-0 items-center gap-1.5 truncate"
        >
          <span
            aria-hidden="true"
            className={`inline-block h-2 w-2 shrink-0 rounded-full ${statusDotClass[status.kind]}`}
          />
          <span className="min-w-0 truncate">{status.text}</span>
        </span>
      </div>

      {menuOpen ? (
        <div
          ref={menuRef}
          data-testid="chat-projects"
          role="dialog"
          aria-label={t.projectsDialog}
          onKeyDown={handleMenuKeyDown}
          className="absolute inset-x-0 top-full z-30 max-h-[60vh] overflow-y-auto rounded-b-md border-b border-l border-r border-border bg-popover text-popover-foreground shadow-lg"
        >
          <ul className="flex flex-col gap-1 p-2">
            {projects.map((project) => {
              const isCurrent = project.id === currentProjectId;
              return (
                <li
                  key={project.id}
                  data-testid={`chat-project-${project.id}`}
                  className="min-w-0"
                >
                  {project.agent ? (
                    <button
                      type="button"
                      data-testid={`chat-project-${project.id}-select`}
                      onClick={() => handleSelect(project.id)}
                      className="flex min-h-11 w-full min-w-0 items-center rounded-md px-2 text-left text-sm font-medium hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <span className="min-w-0 truncate">{project.name}</span>
                    </button>
                  ) : (
                    <div className="flex min-h-11 min-w-0 flex-col justify-center px-2 py-1">
                      <span className="min-w-0 truncate text-sm font-medium">{project.name}</span>
                    </div>
                  )}
                  {project.agent ? (
                    <div
                      data-testid={`chat-conv-${project.id}-general`}
                      aria-current={isCurrent ? "true" : undefined}
                      className={`ml-3 flex min-h-11 min-w-0 items-center rounded-md px-2 text-sm ${
                        isCurrent ? "bg-muted font-medium text-foreground" : "text-muted-foreground"
                      }`}
                    >
                      <span className="min-w-0 truncate">{t.mainConversation}</span>
                    </div>
                  ) : (
                    <div className="px-2 pb-1 text-xs text-muted-foreground">
                      {t.agentUnavailable}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
          <div className="border-t border-border p-2">
            <button
              type="button"
              data-testid="chat-new-conv"
              disabled
              className="flex min-h-11 w-full cursor-not-allowed items-center justify-center rounded-md px-2 text-center text-sm text-muted-foreground opacity-60"
            >
              <span className="min-w-0 truncate">{t.newConversation}</span>
            </button>
          </div>
        </div>
      ) : null}
    </header>
  );
}

function purposeChipText(purpose: ChatPurpose, lang: ChatLang): string {
  const label = purpose.label ?? "";
  if (lang === "en") {
    if (purpose.kind === "decision") return `Decision: ${label}`;
    if (purpose.kind === "department") return `Department: ${label}`;
    return "General conversation";
  }
  if (purpose.kind === "decision") return `Odluka: ${label}`;
  if (purpose.kind === "department") return `Odjel: ${label}`;
  return "Opći razgovor";
}

function useUncontrolledToggle(): [boolean, (next: boolean | ((prev: boolean) => boolean)) => void] {
  const [open, setOpen] = useState(false);
  return [open, setOpen];
}

export default ChatHeader;
